// Offline write queue backed by IndexedDB.
// Enqueues API POSTs (orders, gps points, field-day visits) when the network
// is unavailable and drains them once connectivity returns.
//
// Each queued entry carries a stable `client_id` so backend idempotency
// (see /api/orders, /api/gps/points, /api/daily-report/visit) prevents
// duplicate rows on retry.
import { logger } from "./logger";
import { nowISOIST } from "./datefmt";

const DB_NAME = "taraigold-offline";
const DB_VERSION = 1;
const STORE = "outbox";

const IS_BROWSER = typeof window !== "undefined" && !!window.indexedDB;

function openDb() {
  return new Promise((resolve, reject) => {
    if (!IS_BROWSER) {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("created_at", "created_at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode) {
  const t = db.transaction(STORE, mode);
  return t.objectStore(STORE);
}

function promisify(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function newId() {
  // RFC4122-lite UUIDv4 without external deps.
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const LABELS = {
  "/orders": "Order",
  "/gps/points": "GPS point",
  "/daily-report/visit": "Visit",
};

export function labelForEndpoint(endpoint) {
  return LABELS[endpoint] || endpoint;
}

export async function enqueue({ endpoint, payload }) {
  const clientId = newId();
  const entry = {
    id: clientId,
    endpoint,
    payload: { ...payload, client_id: clientId },
    created_at: nowISOIST(),
    attempts: 0,
    last_error: "",
  };
  try {
    const db = await openDb();
    await promisify(tx(db, "readwrite").put(entry));
    db.close();
  } catch (err) {
    logger.warn("offlineQueue.enqueue failed:", err?.message);
    throw err;
  }
  window.dispatchEvent(new CustomEvent("tg-outbox-changed"));
  return entry;
}

export async function listQueue() {
  if (!IS_BROWSER) return [];
  try {
    const db = await openDb();
    const rows = await promisify(tx(db, "readonly").getAll());
    db.close();
    return rows.sort((a, b) => a.created_at.localeCompare(b.created_at));
  } catch (err) {
    logger.warn("offlineQueue.listQueue failed:", err?.message);
    return [];
  }
}

export async function countQueue() {
  if (!IS_BROWSER) return 0;
  try {
    const db = await openDb();
    const n = await promisify(tx(db, "readonly").count());
    db.close();
    return n;
  } catch {
    return 0;
  }
}

export async function removeEntry(id) {
  const db = await openDb();
  await promisify(tx(db, "readwrite").delete(id));
  db.close();
  window.dispatchEvent(new CustomEvent("tg-outbox-changed"));
}

export async function updateEntry(id, patch) {
  const db = await openDb();
  const store = tx(db, "readwrite");
  const existing = await promisify(store.get(id));
  if (existing) {
    await promisify(store.put({ ...existing, ...patch }));
  }
  db.close();
  window.dispatchEvent(new CustomEvent("tg-outbox-changed"));
}

/**
 * Detect whether an axios error is a network / offline failure that should
 * trigger queueing. HTTP errors from the server (4xx/5xx) should NOT be
 * queued — the caller must see them.
 */
export function isNetworkError(err) {
  if (!err) return false;
  if (err.response) return false; // server responded → not a network issue
  if (err.code === "ERR_NETWORK") return true;
  if (err.code === "ECONNABORTED") return true;
  if (err.message && /network|failed to fetch|timeout/i.test(err.message)) return true;
  return !navigator.onLine;
}

export const QUEUEABLE_ENDPOINTS = new Set(Object.keys(LABELS));
